import { supabase } from './supabaseClient';

// Export supabase for backward compatibility with admin pages
export { supabase };

// Admin Service - Web version
export class AdminService {
  static async getAdminStats() {
    try {
      const { data, error } = await supabase.rpc('admin_get_stats');
      
      if (error) {
        console.error('Failed to get admin stats:', error);
        return {
          totalUsers: 0,
          totalCustomers: 0,
          totalMechanics: 0,
          activeRequests: 0,
          totalRevenue: 0,
          pendingOrders: 0,
          supportTickets: 0,
          totalLeads: 0,
          totalMasterOrders: 0,
          pendingAssignments: 0,
          completedServices: 0,
          todayRequests: 0,
          deliveredLeads: 0,
          uniqueMechanicPincodes: 0,
        };
      }

      const statsRow = data?.[0];
      if (!statsRow) return null;

      return {
        totalUsers: Number(statsRow.total_users) || 0,
        totalCustomers: Number(statsRow.active_users) || 0,
        totalMechanics: 0,
        activeRequests: Number(statsRow.active_requests) || 0,
        totalRevenue: Number(statsRow.total_revenue) || 0,
        pendingOrders: Number(statsRow.pending_orders) || 0,
        supportTickets: Number(statsRow.support_tickets) || 0,
        totalLeads: Number(statsRow.total_leads) || 0,
        totalMasterOrders: Number(statsRow.total_master_orders) || 0,
        pendingAssignments: Number(statsRow.pending_assignments) || 0,
        completedServices: Number(statsRow.completed_services) || 0,
        todayRequests: 0,
        deliveredLeads: Number(statsRow.delivered_leads) || 0,
        uniqueMechanicPincodes: Number(statsRow.unique_mechanic_pincodes) || 0,
      };
    } catch (error) {
      console.error('Error getting admin stats:', error);
      return null;
    }
  }

  static async getSystemStatus() {
    try {
      const { error: dbError } = await supabase.from('users').select('id').limit(1);
      const database = dbError ? 'offline' : 'online';
      return {
        database,
        payments: 'online',
        notifications: 'online',
        chat: 'online',
      };
    } catch (error) {
      return {
        database: 'offline',
        payments: 'offline',
        notifications: 'offline',
        chat: 'offline',
      };
    }
  }

  static async getAllUsers() {
    try {
      const { data, error } = await supabase.rpc('admin_get_all_users_all');
      if (error) {
        console.error('Error getting users:', error);
        return [];
      }
      return data?.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        roles: user.roles || [],
        status: user.status || 'active',
        created_at: user.created_at,
        team_lead_id: user.team_lead_id,
        team_lead_name: user.team_lead_name,
      })) || [];
    } catch (error) {
      console.error('Error getting users:', error);
      return [];
    }
  }

  // Telecaller KYC list (joined view)
  static async getTelecallerKyc() {
    try {
      const { data, error } = await supabase.rpc('admin_list_telecaller_kyc');
      if (error) {
        console.error('Error getting telecaller KYC:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting telecaller KYC:', error);
      return [];
    }
  }

  // Update telecaller KYC admin fields (verified + commission)
  static async updateTelecallerKyc(userId, { is_verified, commission_percent }) {
    try {
      const { data, error } = await supabase.rpc('admin_update_telecaller_kyc', {
        p_user_id: userId,
        p_is_verified: typeof is_verified === 'boolean' ? is_verified : null,
        p_commission_percent: commission_percent === '' || commission_percent === null || commission_percent === undefined
          ? null
          : Number(commission_percent),
      });
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error) {
      console.error('Error updating telecaller KYC:', error);
      return { success: false, error: error.message || 'Failed to update KYC' };
    }
  }

  static async createUser(userData) {
    try {
      // Hash password (simple hash for web - should use proper hashing in production)
      const passwordHash = userData.password; // In production, hash this properly

      const { data, error } = await supabase.rpc('admin_create_user', {
        p_name: userData.name,
        p_email: userData.email,
        p_phone: userData.phone || '',
        p_password: passwordHash,
        p_status: userData.status || 'active',
        p_dialer_user_id: userData.dialerId || null
      });

      if (error) throw error;

      const userId = data?.[0]?.id;
      if (!userId) throw new Error('Failed to get user ID');

      // Assign roles
      if (userData.roles && userData.roles.length > 0) {
        const { data: rolesData } = await supabase.rpc('admin_get_roles_by_names', {
          role_names: userData.roles
        });

        if (rolesData) {
          // NOTE: In many deployments user_roles.user_id is UNIQUE (1 role per user).
          // Keep only the first role to avoid unique-constraint violations.
          const safeRoles = Array.isArray(rolesData) ? rolesData.slice(0, 1) : [];
          const roleAssignments = safeRoles.map(role => ({
            user_id: userId,
            role_id: role.id,
            assigned_by: userData.teamLeaderId || userId
          }));

          const { error: assignError } = await supabase.rpc('admin_assign_roles_to_user', {
            p_user_id: userId,
            // RPC expects jsonb, pass array directly (do NOT JSON.stringify)
            p_role_assignments: roleAssignments
          });
          if (assignError) throw assignError;
        }
      }

      return { success: true, user: data[0] };
    } catch (error) {
      console.error('Error creating user:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateUser(userId, userData) {
    try {
      await supabase.rpc('admin_update_user', {
        p_user_id: userId,
        p_name: userData.name || null,
        p_email: userData.email || null,
        p_phone: userData.phone || null,
        p_status: userData.status || null,
        p_dialer_user_id: userData.dialer_user_id || null
      });

      // Update roles if provided
      if (userData.roles && userData.roles.length > 0) {
        // Delete existing roles
        const { error: delRolesError } = await supabase.rpc('admin_delete_user_roles', {
          p_user_id: userId
        });
        if (delRolesError) throw delRolesError;

        // Get new role IDs
        const { data: rolesData } = await supabase.rpc('admin_get_roles_by_names', {
          role_names: userData.roles
        });

        if (rolesData) {
          // NOTE: In many deployments user_roles.user_id is UNIQUE (1 role per user).
          // Keep only the first role to avoid unique-constraint violations.
          const safeRoles = Array.isArray(rolesData) ? rolesData.slice(0, 1) : [];
          const roleAssignments = safeRoles.map(role => ({
            user_id: userId,
            role_id: role.id,
            assigned_by: userData.teamLeaderId || userId
          }));

          const { error: assignError } = await supabase.rpc('admin_assign_roles_to_user', {
            p_user_id: userId,
            // RPC expects jsonb, pass array directly (do NOT JSON.stringify)
            p_role_assignments: roleAssignments
          });
          if (assignError) throw assignError;
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.message };
    }
  }

  static async deleteUser(userId) {
    try {
      await supabase.rpc('admin_update_user', {
        p_user_id: userId,
        p_status: 'inactive'
      });
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  }

  static async changeTeamLead(telecallerId, newTeamLeadId, changedBy) {
    try {
      // DB function signatures vary across deployments; try most specific first.
      // Canonical (from repo SQL): admin_change_team_lead(telecaller_id, new_team_lead_id, changed_by, old_team_lead_id default null)
      const currentUser = (() => {
        try {
          return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
        } catch {
          return {};
        }
      })();
      const changedByUuid = changedBy && String(changedBy).length > 20 ? changedBy : currentUser?.id || null;

      const attempts = [
        { telecaller_id: telecallerId, new_team_lead_id: newTeamLeadId, changed_by: changedByUuid, old_team_lead_id: null },
        { telecaller_id: telecallerId, new_team_lead_id: newTeamLeadId, old_team_lead_id: null },
        // legacy param names (older frontends)
        { p_telecaller_id: telecallerId, p_new_team_lead_id: newTeamLeadId, p_changed_by: changedByUuid },
      ];

      let lastError = null;
      for (const args of attempts) {
        const { error } = await supabase.rpc('admin_change_team_lead', args);
        if (!error) {
          return { success: true };
        }
        lastError = error;
        // stop early if it's not a signature mismatch
        if (error.code && error.code !== 'PGRST202') break;
      }

      if (lastError) throw lastError;
      return { success: true };
    } catch (error) {
      console.error('Error changing team lead:', error);
      return { success: false, error: error.message };
    }
  }

  static async getMechanicCoverageByState() {
    try {
      const { data, error } = await supabase.rpc('get_mechanic_coverage_by_state');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting mechanic coverage:', error);
      return [];
    }
  }

  static async getUniqueMechanicPincodeCount() {
    try {
      const { data, error } = await supabase.rpc('get_unique_mechanic_pincode_count');
      if (error) throw error;
      return data?.[0]?.count || 0;
    } catch (error) {
      console.error('Error getting unique pincode count:', error);
      return 0;
    }
  }

  // Payout Requests (Admin)
  static async getPayoutRequests(status = null) {
    try {
      const { data, error } = await supabase.rpc('admin_list_payout_requests', {
        p_status: status,
      });
      if (error) {
        console.error('Error getting payout requests:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting payout requests:', error);
      return [];
    }
  }

  static async getPayoutRequestItems(requestId) {
    try {
      const { data, error } = await supabase.rpc('admin_get_payout_request_items', {
        p_request_id: requestId,
      });
      if (error) {
        console.error('Error getting payout request items:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting payout request items:', error);
      return [];
    }
  }

  static async updatePayoutRequest(requestId, status, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_payout_request', {
        p_request_id: requestId,
        p_status: status,
        p_note: note,
      });
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error) {
      console.error('Error updating payout request:', error);
      return { success: false, error: error.message || 'Failed to update payout request' };
    }
  }
}
